// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { CodeSystem } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import { APPOINTMENT_TYPE_SYSTEM, parseAppointmentTypeCodeSystem } from '@polarissleep/scheduling';
import { useEffect, useState } from 'react';

/**
 * Fetches the appointment type CodeSystem and parses it into typed definitions.
 * Mirrors the provider-app hook, using the shared lib for parsing.
 * @returns Appointment type definitions and loading state
 */
export function useAppointmentTypes(): {
  appointmentTypes: AppointmentTypeDefinition[];
  loading: boolean;
} {
  const medplum = useMedplum();
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeDefinition[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchTypes(): Promise<void> {
      try {
        const codeSystem = await medplum.searchOne('CodeSystem', {
          url: APPOINTMENT_TYPE_SYSTEM,
        });
        if (!cancelled && codeSystem) {
          setAppointmentTypes(
            parseAppointmentTypeCodeSystem((codeSystem as CodeSystem).concept ?? []).filter((type) => type.schedulable)
          );
        }
      } catch (err) {
        console.error('Failed to load appointment types:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchTypes().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [medplum]);

  return { appointmentTypes, loading };
}
